// @ts-strict-ignore
import {Component, computed, inject, Injector, OnInit, Signal, signal} from "@angular/core";
import {BasicMaterialDialog, MatDialogDataBaseInput} from "../../basic-material-dialog/basic-material-dialog.directive";
import {
	ExamApplicationDataType,
	ExamParticipantDto,
	ExamStationDto,
	ParticipantType,
	SessionPhaseType,
	SessionRunParticipantStateType,
} from "@models";
import {PhaseTypeMapping, SimExamFlowService, SimMaterialDialogService, SimScenarioService, SimTrainingRoomService} from "@services";
import {EnumLabel} from "../../../../../shared/shared-pipes/enum-label.pipe";
import {SimStatusPalette} from "../../../../../shared/models/SimStatusPalette";
import {PhaseColorMapping} from "../../../../../shared/models/SimSessionPhasePalette";
import {ExamMcStateService} from "../../../../applications/master-of-ceremony-app/service/exam-mc-state.service";
import {
	ExamParticipantDisplayState,
	ExamParticipantDisplayStateMapping,
} from "../../../../applications/master-of-ceremony-app/components/station-card/station-card.utils";

export interface DialogData_StationDetailDialogInput extends MatDialogDataBaseInput {
	examStation: ExamStationDto;
	participantState: Signal<ExamParticipantDisplayStateMapping>;
	examState: Signal<ExamApplicationDataType | null>;
}

@Component({
	selector: "sim-station-detail-dialog",
	templateUrl: "./station-detail-dialog.component.html",
	styleUrl: "./station-detail-dialog.component.scss",
	standalone: false,
})
export class StationDetailDialogComponent
	extends BasicMaterialDialog<StationDetailDialogComponent, DialogData_StationDetailDialogInput>
	implements OnInit
{
	// Properties
	public examStation: ExamStationDto;
	public examiner: ExamParticipantDto | null = null;
	public student: ExamParticipantDto | null = null;
	public standardizedPatient: ExamParticipantDto | null = null;
	public isLoadingScenario = signal<boolean>(false);
	public isLoadingRoom = signal<boolean>(false);
	public busyHelp = signal<Map<ParticipantType, boolean>>(
		new Map([
			[ParticipantType.Instructor, false],
			[ParticipantType.Learner, false],
			[ParticipantType.StandardizedPatient, false],
		]),
	);
	public busyReady = signal<Map<ParticipantType, boolean>>(
		new Map([
			[ParticipantType.Instructor, false],
			[ParticipantType.Learner, false],
			[ParticipantType.StandardizedPatient, false],
		]),
	);
	public phaseTheme = computed<string>(() => this._computePhaseTheme());

	// Readonly
	protected readonly ParticipantType = ParticipantType;
	protected readonly EnumLabel = EnumLabel;
	protected readonly SessionRunParticipantStateType = SessionRunParticipantStateType;
	protected readonly SimStatusPalette = SimStatusPalette;
	protected readonly ExamParticipantDisplayState = ExamParticipantDisplayState;
	protected readonly ExamPhaseType = SessionPhaseType;
	protected readonly ExamApplicationDataType = ExamApplicationDataType;

	// Injections
	private _dialogService = inject(SimMaterialDialogService);
	private _roomService = inject(SimTrainingRoomService);
	private _scenarioService = inject(SimScenarioService);
	private _examFlowService = inject(SimExamFlowService);
	private _stateService = inject(ExamMcStateService);

	constructor(_injector: Injector) {
		super(_injector);
	}

	public ngOnInit() {
		this.examStation = this.data.examStation;
		this.examiner = this.examStation.examinerParticipant;
		this.student = this.examStation.studentParticipant;
		this.standardizedPatient = this.examStation.standardizedPatientParticipant;
	}

	protected async openScenarioDetailDialog(): Promise<void> {
		this.isLoadingScenario.set(true);
		const scenario = await this._scenarioService.getScenarioById(this.examStation.scenarioId);
		this._dialogService.openScenarioInformationDialog({scenario}).then(() => this.isLoadingScenario.set(false));
	}

	protected async openTrainingRoomDetailDialog(): Promise<void> {
		this.isLoadingRoom.set(true);
		const trainingRoom = await this._roomService.getTrainingRoomById(this.examStation.trainingRoom.id);
		this._dialogService.openShowRoomDetailDialog({trainingRoom}).then(() => this.isLoadingRoom.set(false));
	}

	protected toggleReady(participant: ExamParticipantDto) {
		this.busyReady.update((map) => map.set(participant.participantType, true));
		const newState =
			this.data.participantState()[participant.participantType] === SessionRunParticipantStateType.PendingLogin
				? SessionRunParticipantStateType.Ready
				: SessionRunParticipantStateType.PendingLogin;
		this._examFlowService
			.setParticipantRunState(this._stateService?.state()?.examSessionId, participant.userId, newState)
			.then(() => this.busyReady.update((map) => map.set(participant.participantType, false)));
	}

	protected async resolveHelpRequest(participant: ExamParticipantDto) {
		this.busyHelp.update((map) => map.set(participant.participantType, true));
		this._examFlowService
			.resolveSupportRequestForParticipant(this._stateService?.state()?.examSessionId, participant.userId)
			.then(() => this.busyHelp.update((map) => map.set(participant.participantType, false)));
	}

	private _computePhaseTheme(): string {
		const phaseType = this.data.examState() in PhaseTypeMapping ? PhaseTypeMapping[this.data.examState()] : -1;
		return PhaseColorMapping[phaseType as SessionPhaseType];
	}
}
